import { useState, useEffect } from 'react';
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import { updateOnboardingStep } from '../../functions/updateOnboarding';
import TextField from "@mui/material/TextField";
import * as theme from '../../theme';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import ErrorDisplay from '../../components/ErrorDisplay';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ConfirmationModal from '../../components/ConfirmationModal';
import { sendEmail } from "../../functions/endpoints/SendEmail";

export default function ScheduleCall() {
  const [timeBlocks, setTimeBlocks] = useState([
    { date: null, timeBlock: '' },
    { date: null, timeBlock: '' },
    { date: null, timeBlock: '' },
  ]);
  const [description, setDescription] = useState('');
  const [isOnboardingCall, setIsOnboardingCall] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (id != null) {
      getPractitioner();
    }
  }, [id]);

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(practitionerRef);
      const practitionerData = docSnap.data();
      if (practitionerData) {
        setIsOnboardingCall(!practitionerData.onboarding.completeCall?.dateCompleted);
      }
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }
  }

  const handleDateChange = (index, value) => {
    const updatedBlocks = [...timeBlocks];
    updatedBlocks[index].date = value;
    setTimeBlocks(updatedBlocks);
  };

  const handleTimeBlockChange = (index, value) => {
    const updatedBlocks = [...timeBlocks];
    updatedBlocks[index].timeBlock = value;
    setTimeBlocks(updatedBlocks);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate at least one time block is filled
    const filledBlocks = timeBlocks.filter(block => block.date && block.timeBlock);
    if (filledBlocks.length === 0) {
      setError("Please provide at least one available time block.");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError("");

    try {
      // Get practitioner details for email
      const practitionerRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(practitionerRef);
      const practitionerData = docSnap.data();
      const practitionerName = `${practitionerData.firstName} ${practitionerData.lastName}`;

      // Format time blocks
      const formattedTimeBlocks = timeBlocks
        .filter(block => block.date && block.timeBlock)
        .map(block => `${block.date.format('MMM D')} in the ${block.timeBlock}`);

      // Send email
      const recipientEmail = process.env.REACT_APP_ENVIRONMENT === 'PROD' 
        ? 'hello@trubalancebioenergetics.com' 
        : "jhatlestad@gmail.com";

      const emailData = {
        recipientEmail,
        recipientName: "TruBalance Admin",
        subject: isOnboardingCall ? "Onboarding Call Request" : "Support Call Request",
        templateId: "support-call-request",
        dynamicTemplateData: {
          practitionerName,
          practitionerPhone: practitionerData.phone,
          practitionerEmail: practitionerData.email,
          callType: isOnboardingCall ? "Onboarding" : "Support",
          description: isOnboardingCall ? "Onboarding Call" : description,
          timeBlock1: formattedTimeBlocks[0],
          timeBlock2: formattedTimeBlocks[1],
          timeBlock3: formattedTimeBlocks[2]
        },
        emailType: "support_call_request",
        contextData: {
          practitionerId: id,
        },
        userId: id,
        isTest: false
      };

      await sendEmail(emailData);

      // If onboarding call, update onboarding step
      if (isOnboardingCall) {
        await updateOnboardingStep({
          practitionerId: id,
          step: 'scheduleCall',
        });
      }

      navigateBack();
    } catch (err) {
      setError("Failed to submit call request: " + err.message);
    }

    setLoading(false);
  };

  function navigateBack() {
    navigate(`/dashboard`);
  }

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>
        {isOnboardingCall ? "Schedule Onboarding Call" : "Schedule Support Call"}
      </div>
      <div style={theme.SubHeadingText}>
        {isOnboardingCall 
          ? "Provide up to 3 available time blocks in the next 2 weeks for your onboarding call with a TruBalance team member."
          : "Provide up to 3 available time blocks in the next 2 weeks and a description of why you need to schedule a call."}
      </div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          {timeBlocks.map((block, index) => (
            <Box key={index} sx={{ ...theme.FullForm.form, gap: 2 }}>
              <div style={{ paddingLeft: 6 }}>Time Block {index + 1}</div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label={`Select Date`}
                  value={block.date}
                  onChange={(value) => handleDateChange(index, value)}
                  minDate={dayjs().add(1, 'day')}
                  maxDate={dayjs().add(14, 'day')}
                  slotProps={{
                    textField: {
                      variant: "filled",
                    },
                  }}
                  disabled={loading}
                />
              </LocalizationProvider>
              <FormControl variant="filled" fullWidth>
                <InputLabel>Time Block</InputLabel>
                <Select
                  value={block.timeBlock}
                  onChange={(e) => handleTimeBlockChange(index, e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="Morning (8am-12pm CST)">Morning (8am-12pm CST)</MenuItem>
                  <MenuItem value="Afternoon (12pm-4pm CST)">Afternoon (12pm-4pm CST)</MenuItem>
                  <MenuItem value="Evening (4pm-8pm CST)">Evening (4pm-8pm CST)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          ))}
          {!isOnboardingCall && (
            <TextField
              label="Description of Issue/Reason for Call"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="filled"
              multiline
              rows={4}
              fullWidth
              disabled={loading}
            />
          )}
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigateBack()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmSave}
        title="Confirm Submission"
        description="Are you sure you want to submit this call request?"
        confirmButtonText="Submit"
      />
    </div>
  );
}